import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  NgZone,
  Signal,
  signal,
} from '@angular/core';
import { Subscription } from 'rxjs';

import {
  makeColumnGridStyle,
  PropertiesConfigInputTableColumnModel,
} from '@blaze-shared/elements-domain';
import { resizeRxUtils, runAfterFramePaint } from '@blaze-shared/shared-util';

import {
  ACTIONS_COLUMN_DEFAULT_WIDTH,
  ACTIONS_COLUMN_NAME,
  COLUMN_DEFAULT_WIDTH,
  SELECT_COLUMN_DEFAULT_WIDTH,
  SELECT_COLUMN_NAME,
} from './injection-tokens';
import { Slot, VirtualTableColumn } from './models/virtual-table-column.model';
import {
  calculateWidths,
  filterColumnsBySlot,
  getHeaderRowTemplateColumns,
  getMaxWidthFromElements,
} from './utils/column-width.utils';

@Injectable()
export class TableVirtualScrollFacade {
  private readonly actionsColumnName: string = inject(ACTIONS_COLUMN_NAME);
  private readonly selectColumnName: string = inject(SELECT_COLUMN_NAME);
  private readonly defaultColumnWidth: number = inject(COLUMN_DEFAULT_WIDTH);
  private readonly selectColumnDefaultWidth: number = inject(SELECT_COLUMN_DEFAULT_WIDTH);
  private readonly actionsColumnDefaultWidth: number = inject(ACTIONS_COLUMN_DEFAULT_WIDTH);
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly _headerCells = signal<HTMLElement[]>([]);
  private readonly _rowActionCells = signal<Record<string, HTMLElement>>({});
  private readonly _actionsContentMinWidth = signal<number>(0);
  private readonly _headerActionCellWidth = signal<number>(0);
  private readonly _gridTemplateColumns = signal<string[]>([]);
  private readonly _cellsWidth = signal<number[]>([]);
  private readonly _firstColumnsAlwaysVisible = signal<number>(0);
  private readonly _lastColumnAlwaysVisible = signal<boolean>(false);
  private readonly _displayedColumns = signal<string[]>([]);
  private readonly _displayedColumnsDefs = signal<PropertiesConfigInputTableColumnModel[]>([]);
  private readonly _rootElementResize = signal<number>(0);
  private readonly _viewportRefresh = signal<number>(0);
  private readonly _headerRowGridTemplateColumns = signal<string>('1fr');
  private readonly _headerRowWidth = signal<number | undefined>(undefined);

  private readonly cellsWidthBySlot = new Map<Slot | 'all', Signal<number[]>>();
  private readonly rowWidthBySlot = new Map<Slot | 'all', Signal<number>>();
  private readonly columnsBySlot = new Map<Slot | 'all', Signal<VirtualTableColumn[]>>();
  private readonly gridTemplateColumnsBySlot = new Map<Slot, Signal<string>>();

  private readonly cellTypes: Record<string, 'select' | 'actions' | 'cell'> = {
    [this.selectColumnName]: 'select',
    [this.actionsColumnName]: 'actions',
  };

  private resizeSub: Subscription = Subscription.EMPTY;
  private propsChangeSub: Subscription = Subscription.EMPTY;

  private tableElement: HTMLElement | null = null;
  private containerElement: HTMLElement | null = null;

  private isColumnResizing = false;
  private dragTableWidth: number | null = null;

  readonly displayedColumnsCount = computed((): number => {
    return this._displayedColumns().length;
  });
  readonly firstColumnsAlwaysVisible = computed((): number => {
    return this._firstColumnsAlwaysVisible();
  });
  readonly lastColumnAlwaysVisible = computed((): boolean => {
    return this._lastColumnAlwaysVisible();
  });
  readonly rootElementResize = computed((): number => {
    return this._rootElementResize();
  });
  readonly viewportRefresh = computed((): number => {
    return this._viewportRefresh();
  });
  readonly headerRowGridTemplateColumns = computed((): string => {
    return this._headerRowGridTemplateColumns();
  });
  readonly headerRowWidth = computed((): number | undefined => {
    return this._headerRowWidth();
  });
  readonly hasActionsColumn = computed((): boolean => {
    return this._displayedColumns().includes(this.actionsColumnName);
  });
  readonly actionsCellMaxWidth = computed((): number => {
    return Math.max(this._headerActionCellWidth(), this._actionsContentMinWidth());
  });

  private get availableWidth(): number {
    const container = this.containerElement ?? this.tableElement;
    return container?.getBoundingClientRect().width ?? 0;
  }

  constructor() {
    this.destroyRef.onDestroy((): void => {
      this.onDestroy();
    });

    effect((): void => {
      this._firstColumnsAlwaysVisible();
      this._lastColumnAlwaysVisible();
      this._displayedColumns();
      this._displayedColumnsDefs();

      if (this.tableElement) {
        this.updateColumnsWidth();
      }
    });
  }

  cellsWidth(slot?: Slot): Signal<number[]> {
    const key: Slot | 'all' = slot ?? 'all';

    const cachedSignal = this.cellsWidthBySlot.get(key);

    if (cachedSignal) {
      return cachedSignal;
    }

    const signal: Signal<number[]> = computed((): number[] => {
      const cells = this._cellsWidth();

      if (!slot) {
        return cells;
      }

      const columns = this._displayedColumns();
      const first = this._firstColumnsAlwaysVisible();
      const last = this._lastColumnAlwaysVisible();

      const { indexes } = filterColumnsBySlot(
        slot,
        columns,
        first,
        last,
        this.selectColumnName,
        this.actionsColumnName,
      );
      return cells.filter((_, index: number) => indexes.includes(index));
    });

    this.cellsWidthBySlot.set(key, signal);
    return signal;
  }

  rowWidth(slot?: Slot): Signal<number> {
    const key: Slot | 'all' = slot ?? 'all';

    const cachedSignal = this.rowWidthBySlot.get(key);

    if (cachedSignal) {
      return cachedSignal;
    }

    const signal: Signal<number> = computed((): number => {
      const widths: number[] = this.cellsWidth(slot)();
      if (widths.length === 0) {
        return 0;
      }
      return widths.reduce((sum: number, w: number) => sum + w, 0);
    });

    this.rowWidthBySlot.set(key, signal);
    return signal;
  }

  columns(slot?: Slot): Signal<VirtualTableColumn[]> {
    const key: Slot | 'all' = slot ?? 'all';

    const cachedSignal = this.columnsBySlot.get(key);

    if (cachedSignal) {
      return cachedSignal;
    }

    const signal: Signal<VirtualTableColumn[]> = computed((): VirtualTableColumn[] => {
      const displayed: string[] = this._displayedColumns();
      const defs: PropertiesConfigInputTableColumnModel[] = this._displayedColumnsDefs();
      const first: number = this._firstColumnsAlwaysVisible();
      const last: boolean = this._lastColumnAlwaysVisible();

      let names: string[] = displayed;
      if (slot) {
        names = filterColumnsBySlot(
          slot,
          displayed,
          first,
          last,
          this.selectColumnName,
          this.actionsColumnName,
        ).columns;
      }

      return names.map((name: string): VirtualTableColumn => {
        const def = defs.find((d) => d.name === name);
        const type = this.cellTypes[name] ?? 'cell';
        return { name, def, type };
      });
    });

    this.columnsBySlot.set(key, signal);
    return signal;
  }

  gridTemplateColumns(slot: Slot): Signal<string> {
    const cachedSignal = this.gridTemplateColumnsBySlot.get(slot);

    if (cachedSignal) {
      return cachedSignal;
    }

    const signal: Signal<string> = computed((): string => {
      const columns: string[] = this._displayedColumns();
      const styles: string[] = this._gridTemplateColumns();
      const first: number = this._firstColumnsAlwaysVisible();
      const last: boolean = this._lastColumnAlwaysVisible();

      const { indexes } = filterColumnsBySlot(
        slot,
        columns,
        first,
        last,
        this.selectColumnName,
        this.actionsColumnName,
      );
      return styles.filter((_, index: number) => indexes.includes(index)).join(' ');
    });

    this.gridTemplateColumnsBySlot.set(slot, signal);
    return signal;
  }

  registerRootElement(
    resizeElement: HTMLElement,
    tableElement: HTMLElement,
    containerElement: HTMLElement,
  ): void {
    this.tableElement = tableElement;
    this.containerElement = containerElement;

    this.resizeSub.unsubscribe();
    this.resizeSub = Subscription.EMPTY;

    this.resizeSub = resizeRxUtils
      .fromResizeObserver(resizeElement)
      .subscribe((entries: ResizeObserverEntry[]) => {
        const entry: ResizeObserverEntry | undefined = entries.at(-1);
        if (!entry) {
          return;
        }

        const newWidth: number = this.availableWidth;
        if (!newWidth) {
          return;
        }

        this.updateColumnsWidth(newWidth);
        this._rootElementResize.set(newWidth);

        this.ngZone.runOutsideAngular((): void => {
          runAfterFramePaint((): void => {
            this.raiseActionsContentFloor();
          });
        });
      });

    this.propsChangeSub.unsubscribe();
    this.propsChangeSub = Subscription.EMPTY;
  }

  updateDisplayedColumns(columns: string[], defs: PropertiesConfigInputTableColumnModel[]): void {
    this._displayedColumns.set(columns);
    this._displayedColumnsDefs.set(defs);

    this.updateGridTemplateColumns();
  }

  updateHeaderCells(elements: HTMLElement[]): void {
    this._headerCells.set(elements);
  }

  updateRowActionCells(id: string, element: HTMLElement): void {
    const map = { ...this._rowActionCells() };
    map[id] = element;
    this._rowActionCells.set(map);

    this.raiseActionsContentFloor();
  }

  updateStickyColumnsVisibility(
    firstColumnsAlwaysVisible: number,
    lastColumnAlwaysVisible: boolean,
  ): void {
    this._firstColumnsAlwaysVisible.set(firstColumnsAlwaysVisible);
    this._lastColumnAlwaysVisible.set(lastColumnAlwaysVisible);
  }
  updateGridTemplateColumns(): void {
    if (this.isColumnResizing) {
      return;
    }

    const displayedColumns = this._displayedColumns();
    const defs = this._displayedColumnsDefs();
    const rowActionMax = this._headerActionCellWidth() || undefined;
    const lastColumnIsActions = displayedColumns.at(-1) === this.actionsColumnName;

    const stretchIndex = lastColumnIsActions ? -1 : displayedColumns.length - 1;

    this._gridTemplateColumns.set(
      displayedColumns.map((column, index) => {
        const def = defs.find((d) => d.name === column);
        const stretchToFill = index === stretchIndex;
        return makeColumnGridStyle(
          column,
          displayedColumns.length,
          def,
          rowActionMax,
          stretchToFill,
        );
      }),
    );
  }
  onDestroy(): void {
    this.resizeSub.unsubscribe();
    this.propsChangeSub.unsubscribe();

    this.resizeSub = Subscription.EMPTY;
    this.propsChangeSub = Subscription.EMPTY;

    this.cellsWidthBySlot.clear();
    this.rowWidthBySlot.clear();
    this.columnsBySlot.clear();
    this.gridTemplateColumnsBySlot.clear();
  }
  columnsForSlot(slot?: Slot): VirtualTableColumn[] {
    let columns = this._displayedColumns();
    const defs = this._displayedColumnsDefs();
    const first = this._firstColumnsAlwaysVisible();
    const last = this._lastColumnAlwaysVisible();

    if (slot) {
      columns = filterColumnsBySlot(
        slot,
        columns,
        first,
        last,
        this.selectColumnName,
        this.actionsColumnName,
      ).columns;
    }

    return columns.map((name) => {
      const def = defs.find((d) => d.name === name);
      const type = this.cellTypes[name] ?? 'cell';
      return { name, def, type };
    });
  }
  updateColumnsWidth(tableWidth?: number, actionsWidth?: number): void {
    if (!this.tableElement) {
      throw new Error('Table element was not registered');
    }

    if (this.isColumnResizing) {
      return;
    }

    this.applyColumnWidths(tableWidth, actionsWidth);
    this.updateGridTemplateColumns();
  }

  applyLiveColumnWidths(gridStyles: string[], columnWidths: number[]): void {
    if (!this.tableElement) {
      return;
    }

    if (!this.isColumnResizing) {
      this.isColumnResizing = true;
      this.dragTableWidth = this.availableWidth;
    }

    this._gridTemplateColumns.set(gridStyles);
    this.applyColumnWidths(this.dragTableWidth ?? undefined, undefined, columnWidths);
  }

  commitColumnResize(): void {
    if (!this.isColumnResizing) {
      return;
    }

    this.isColumnResizing = false;
    this.dragTableWidth = null;
    this.updateColumnsWidth();

    this.ngZone.runOutsideAngular((): void => {
      runAfterFramePaint((): void => {
        this.raiseActionsContentFloor();
      });
    });
  }

  private raiseActionsContentFloor(): void {
    const measured = getMaxWidthFromElements(Object.values(this._rowActionCells()));
    const known = Math.max(this._headerActionCellWidth(), this._actionsContentMinWidth());

    if (measured > known) {
      this._actionsContentMinWidth.set(measured);
      this.updateColumnsWidth();
    }
  }

  private applyColumnWidths(
    tableWidth?: number,
    actionsWidth?: number,
    forcedColumnsWidth?: number[],
  ): void {
    const forcedColumns = forcedColumnsWidth?.slice();

    tableWidth ??= this.availableWidth;
    actionsWidth ??= this._actionsContentMinWidth() || undefined;

    const displayedColumns = this._displayedColumns();
    const displayedColumnsDefs = this._displayedColumnsDefs();

    const selectColumnExists = displayedColumns.includes(this.selectColumnName);
    const actionsColumnExists = displayedColumns.includes(this.actionsColumnName);

    const displayedColumnsDefsWidths = displayedColumnsDefs.map((c) => c.width);

    if (forcedColumns) {
      if (selectColumnExists) {
        forcedColumns.shift();
      }

      for (let index = 0; index < forcedColumns.length; index++) {
        displayedColumnsDefsWidths[index] = forcedColumns[index];
      }
    }

    const result = calculateWidths({
      tableWidth,
      columnWidths: displayedColumnsDefsWidths,
      selectColumnExists,
      actionsColumnExists,
      actionsWidth,
      displayedColumns,
      defaultColumnWidth: this.defaultColumnWidth,
      selectColumnDefaultWidth: this.selectColumnDefaultWidth,
      actionsColumnDefaultWidth: this.actionsColumnDefaultWidth,
    });

    const headerRowStyles = getHeaderRowTemplateColumns(
      result.cellsWidth,
      displayedColumns,
      this.selectColumnName,
      this.actionsColumnName,
      this._firstColumnsAlwaysVisible(),
      this._lastColumnAlwaysVisible(),
    );

    this._headerRowGridTemplateColumns.set(headerRowStyles);
    this._cellsWidth.set(result.cellsWidth);
    this._headerRowWidth.set(result.cellsWidth.reduce((sum: number, w: number) => sum + w, 0));
    this._headerActionCellWidth.set(result.headerActionsWidth);

    this._viewportRefresh.update((value) => value + 1);
  }
}
